import { useEffect, useState } from 'react';
import { Tag, ChevronDown } from 'lucide-react';
import type { DiscountType } from '@/lib/b2bDiscounts';

const fmt = (n: number) =>
  `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function DiscountValueInput({
  type,
  value,
  maxAmount,
  disabled,
  onChange,
  size = 'sm',
}: {
  type:      DiscountType;
  value:     number;
  maxAmount: number;
  disabled?: boolean;
  onChange:  (type: DiscountType, value: number) => void;
  size?:     'sm' | 'md';
}) {
  const [text, setText] = useState(value ? String(value) : '');

  useEffect(() => {
    setText(prev => (Number(prev || 0) === value ? prev : value ? String(value) : ''));
  }, [value]);

  const limit = type === 'pct' ? 100 : maxAmount;

  const commit = (raw: string, t: DiscountType) => {
    const n = Number(raw);
    if (raw === '' || Number.isNaN(n)) { onChange(t, 0); return; }
    const max = t === 'pct' ? 100 : maxAmount;
    onChange(t, Math.min(Math.max(0, Math.round(n * 100) / 100), max));
  };

  const pad = size === 'md' ? 'py-1.5 text-sm' : 'py-1 text-xs';

  return (
    <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden
                    focus-within:ring-2 focus-within:ring-emerald-300 transition-shadow">
      <input
        type="number"
        inputMode="decimal"
        min={0}
        max={limit}
        step="0.01"
        value={text}
        placeholder="0"
        disabled={disabled}
        onChange={e => { setText(e.target.value); commit(e.target.value, type); }}
        onBlur={() => setText(value ? String(value) : '')}
        className={`w-16 px-2 ${pad} text-center font-semibold text-gray-900 tabular-nums
                    bg-transparent focus:outline-none disabled:opacity-50`}
        aria-label="ערך הנחה"
      />
      <div className="flex border-r border-gray-200">
        {(['pct', 'amount'] as const).map(t => (
          <button
            key={t}
            type="button"
            disabled={disabled}
            onClick={() => { if (t !== type) commit(text, t); }}
            className={`px-2 ${pad} font-semibold transition-colors disabled:opacity-50
                        ${t === type ? 'bg-emerald-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
            aria-pressed={t === type}
          >
            {t === 'pct' ? '%' : '₪'}
          </button>
        ))}
      </div>
    </div>
  );
}

export function OrderDiscountPanel({
  editable,
  type,
  value,
  reason,
  maxAmount,
  disabled,
  onChange,
  onReasonChange,
}: {
  editable:       boolean;
  type:           DiscountType;
  value:          number;
  reason:         string;
  maxAmount:      number;
  disabled?:      boolean;
  onChange:       (type: DiscountType, value: number) => void;
  onReasonChange: (reason: string) => void;
}) {
  const hasDiscount = value > 0 || reason.trim() !== '';
  const [open, setOpen] = useState(hasDiscount);

  if (!editable) {
    if (!hasDiscount) return null;
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-100 text-xs text-emerald-800">
        <Tag className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div className="space-y-0.5">
          {value > 0 && <p>הנחה כללית: {type === 'pct' ? `${value}%` : fmt(value)}</p>}
          {reason.trim() && <p>סיבת הנחה: {reason}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700
                   hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-emerald-600" />
          הנחה כללית וסיבת הנחה
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 bg-gray-50/60">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500">הנחה על כל ההזמנה (לפני מע״מ)</span>
            <DiscountValueInput
              type={type}
              value={value}
              maxAmount={maxAmount}
              disabled={disabled}
              onChange={onChange}
              size="md"
            />
          </div>
          <input
            type="text"
            value={reason}
            maxLength={200}
            disabled={disabled}
            onChange={e => onReasonChange(e.target.value)}
            placeholder="סיבת הנחה, למשל שם המבצע"
            className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white
                       focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-50"
          />
        </div>
      )}
    </div>
  );
}
